import React, { createContext, useContext } from 'react';
import getLogger from '@ignitesales/logger-es';
import PropTypes from 'prop-types';
import { Base } from './QuestionGroups';
import { Calculated } from './QuestionTypes';
import {defaultHtmlFormatValues, useNeuroMessages} from './MessageContext';
import {QuestionAnswersToolTip} from './Tooltip';

const logger = getLogger({ component: 'QuestionContext' });
/**
 * @typedef QuestionContextObject
 * @property {function(obj:Object)} getQuestionAnswerMessage - returns text for question answer key
 * @property {function(obj:Object)} getQuestionAnswersArray - returns list of answer options objects for rendering
 * @property {function(type:string)} getQuestionGroupComponent - returns question group component
 * @property {function(type:string)} getQuestionComponent - returns question component
 * @property {Object} questionGroupTypes - question group values
 * @property {Object} questionTypes - question type values
 * @type QuestionContextObject
 */
const defaultValue = {};
const QuestionContext = createContext(defaultValue);

const QuestionContextProvider = ({
  children,
  questionGroupTypes,
  questionTypes,
}) => {
  const messageContext = useNeuroMessages();
  const defaultQuestionGroupComponent = Base;
  const defaultQuestionComponent = Calculated;

  const getQuestionGroupComponent = (type) => {
    const component = questionGroupTypes?.[type];
    if (!component) {
      logger.warn('Question group type not found, using default', type);
    }
    return component || defaultQuestionGroupComponent;
  };

  const getQuestionComponent = (type) => {
    //mm-web specifically looks for lower case 'calculated' type in database...hence:
    const fileName = type.toLowerCase() === 'calculated' ? 'Calculated' : type;
    const component = questionTypes?.[fileName];
    if (!component) {
      logger.warn('Question type not found, using default', fileName);
    }
    return component || defaultQuestionComponent;
  };

  const getQuestionAnswerMessage = ({ questionKey, answerKey, customTag = 'span' }) => {
    let messageKey = `${questionKey}.${answerKey}`;
    return messageContext.formatIntlMessage(
      { id: messageKey, tagName: customTag },
      { ...defaultHtmlFormatValues }
    );
  };

  const getQuestionAnswersArray = ({ question, customTag = 'span' }) => {
    return (question.answers || []).map((answer) =>
      Object.assign(
        {},
        {
          key: `${question.appkey}.${answer.key}`,
          label: getQuestionAnswerMessage({ questionKey: question.appkey, answerKey: answer.key, customTag }),
          value: answer.key,
          subtext: messageContext.getMessage(`${question.appkey}.${answer.key}.subtext`) || '',
          tooltip: messageContext.hasMessage(`${question.appkey}.${answer.key}.tooltip`)
            ? <QuestionAnswersToolTip questionKey={ question.appkey } answerKey={ answer.key } attributes={ answer?.attributes } />
            : null
        }
      )
    );
  };


  /**
   * @type QuestionContextObject
   */
  const contextValue = {
    getQuestionAnswerMessage,
    getQuestionAnswersArray,
    getQuestionGroupComponent,
    getQuestionComponent,
    questionGroupTypes,
    questionTypes,
  };
  return (
    <QuestionContext.Provider value={contextValue}>
      {children}
    </QuestionContext.Provider>
  );
};

/**
 * @returns QuestionContextObject
 */
const useQuestionContext = () => {
  return useContext(QuestionContext);
};

QuestionContextProvider.propTypes = {
  children: PropTypes.node,
  questionGroupTypes: PropTypes.object.isRequired,
  questionTypes: PropTypes.object.isRequired,
};
export { QuestionContextProvider, useQuestionContext };
